import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, TrendingUp, TrendingDown, PoundSterling, Wrench,
  AlertTriangle, CheckCircle, Users, Calendar, Loader2,
  BarChart3, PieChart, Activity, Clock, Star, Hammer
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, LineChart, Line, AreaChart, Area
} from "recharts";
import { API } from "@/App";

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const STATUS_COLORS = {
  'Open': '#ef4444',
  'In Progress': '#f59e0b',
  'Completed': '#10b981',
  'Awaiting Parts': '#8b5cf6',
  'Awaiting Approval': '#3b82f6'
};

export const MaintenanceAnalyticsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/maintenance/analytics`, { headers });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  const statusData = analytics?.status_breakdown || [];
  const monthlyData = analytics?.monthly_trends || [];
  const categoryData = analytics?.category_breakdown || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-600 to-amber-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/admin")}
              className="text-white hover:bg-amber-500"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Maintenance Analytics</h1>
              <p className="text-amber-100">Job performance & contractor metrics</p>
            </div>
          </div>
        </div>
      </header>

      {/* KPI Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Open Jobs</p>
                  <p className="text-2xl font-bold">{analytics?.open_jobs || 0}</p>
                </div>
                <Wrench className="w-8 h-8 text-red-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                <Badge variant="destructive">{analytics?.urgent_jobs || 0} urgent</Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                  <p className="text-2xl font-bold">{analytics?.in_progress || 0}</p>
                </div>
                <Hammer className="w-8 h-8 text-amber-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                <span className="text-muted-foreground">
                  {analytics?.awaiting_parts || 0} awaiting parts
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed MTD</p>
                  <p className="text-2xl font-bold">{analytics?.completed_mtd || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                <span className="text-emerald-600">
                  {analytics?.completion_rate || 0}% completion rate
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Response</p>
                  <p className="text-2xl font-bold">{analytics?.avg_response_hours || 0}h</p>
                </div>
                <Clock className="w-8 h-8 text-blue-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                {analytics?.avg_response_hours <= 24 ? (
                  <span className="text-emerald-600 flex items-center">
                    <TrendingDown className="w-4 h-4 mr-1" /> Good
                  </span>
                ) : (
                  <span className="text-amber-600 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-1" /> Needs improvement
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="contractors">Contractors</TabsTrigger>
            <TabsTrigger value="costs">Costs</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Jobs by Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-amber-600" />
                    Jobs by Status
                  </CardTitle>
                  <CardDescription>Current job distribution</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPie>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Monthly Trends */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-600" />
                    Monthly Job Volume
                  </CardTitle>
                  <CardDescription>Jobs reported vs completed</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="reported" 
                        stroke="#3b82f6" 
                        fill="#93c5fd"
                        name="Reported"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="completed" 
                        stroke="#10b981" 
                        fill="#6ee7b7"
                        name="Completed"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Jobs by Category */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    Jobs by Category
                  </CardTitle>
                  <CardDescription>Most common maintenance types</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={categoryData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="category" type="category" width={100} fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8b5cf6" name="Jobs" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Response Time Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-600" />
                    Response Time Trend
                  </CardTitle>
                  <CardDescription>Average hours to first response</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis unit="h" />
                      <Tooltip formatter={(value) => `${value} hours`} />
                      <Line 
                        type="monotone" 
                        dataKey="avg_response" 
                        stroke="#f59e0b" 
                        strokeWidth={2}
                        dot={{ fill: '#f59e0b' }}
                        name="Avg Response Time"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Contractors Tab */}
          <TabsContent value="contractors">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Top Contractors */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-emerald-600" />
                    Top Contractors
                  </CardTitle>
                  <CardDescription>By completed jobs this month</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(analytics?.top_contractors || []).map((contractor, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                            {idx + 1}
                          </div>
                          <div>
                            <p className="font-medium">{contractor.name}</p>
                            <p className="text-sm text-muted-foreground">{contractor.specialty}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{contractor.jobs_completed}</p>
                          <div className="flex items-center text-amber-500">
                            <Star className="w-4 h-4 fill-current" />
                            <span className="ml-1 text-sm">{contractor.rating || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {(!analytics?.top_contractors || analytics.top_contractors.length === 0) && (
                      <p className="text-center text-muted-foreground py-8">No contractor data</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Contractor Performance */}
              <Card>
                <CardHeader>
                  <CardTitle>Contractor Performance</CardTitle>
                  <CardDescription>Average metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Avg Jobs per Contractor</p>
                        <p className="text-2xl font-bold">{analytics?.avg_jobs_per_contractor || 0}</p>
                      </div>
                      <Wrench className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Avg Response Time</p>
                        <p className="text-2xl font-bold">{analytics?.avg_contractor_response || 0}h</p>
                      </div>
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Avg Completion Time</p>
                        <p className="text-2xl font-bold">{analytics?.avg_completion_days || 0} days</p>
                      </div>
                      <CheckCircle className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Active Contractors */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Contractor Activity Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-700">{analytics?.total_contractors || 0}</p>
                      <p className="text-sm text-muted-foreground">Total Contractors</p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-700">{analytics?.active_contractors || 0}</p>
                      <p className="text-sm text-muted-foreground">Active This Month</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-700">{analytics?.jobs_with_contractors || 0}</p>
                      <p className="text-sm text-muted-foreground">Jobs Assigned</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-700">{analytics?.unassigned_jobs || 0}</p>
                      <p className="text-sm text-muted-foreground">Unassigned Jobs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Costs Tab */}
          <TabsContent value="costs">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Cost Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PoundSterling className="w-5 h-5 text-emerald-600" />
                    Cost by Category
                  </CardTitle>
                  <CardDescription>Total spend breakdown</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPie>
                      <Pie
                        data={analytics?.cost_by_category || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      >
                        {(analytics?.cost_by_category || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `£${value.toLocaleString()}`} />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Monthly Spend */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    Monthly Spend Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => `£${(value / 1000).toFixed(0)}K`} />
                      <Tooltip formatter={(value) => `£${value.toLocaleString()}`} />
                      <Bar dataKey="cost" fill="#10b981" name="Total Cost" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Cost Summary */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Cost Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">£{((analytics?.total_cost_mtd || 0) / 1000).toFixed(1)}K</p>
                      <p className="text-sm text-muted-foreground">This Month</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">£{((analytics?.total_cost_ytd || 0) / 1000).toFixed(1)}K</p>
                      <p className="text-sm text-muted-foreground">Year to Date</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">£{(analytics?.avg_job_cost || 0).toFixed(0)}</p>
                      <p className="text-sm text-muted-foreground">Avg Job Cost</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">£{(analytics?.highest_job_cost || 0).toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">Highest Job</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default MaintenanceAnalyticsPage;
